import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/authContext';
import api from '../../services/api';
import logger from '../../utils/logger';
import './Login.css';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('demo');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const body = { email, password };
      // Send tenant_slug unless the field is blank (super admin login)
      if (tenantSlug.trim()) body.tenant_slug = tenantSlug.trim();
      const res = await api.post('/auth/login', body);
      login(res.data.access_token);
      logger.info('auth', 'Login successful', { email, tenant: tenantSlug.trim() || 'super_admin' });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid credentials. Please try again.';
      logger.warn('auth', 'Login failed', { status: err.response?.status, email, tenant: tenantSlug.trim() || 'super_admin' });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      {/* Ambient background */}
      <div className="login-bg">
        <div className="login-bg-orb login-bg-orb--1" />
        <div className="login-bg-orb login-bg-orb--2" />
        <div className="login-bg-grid" />
      </div>

      {/* Left — decorative panel */}
      <div className="login-panel login-panel--left">
        <div className="login-panel-content animate-fade-up">
          <div className="login-panel-logo">
            <div className="login-panel-logo-mark">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L21 8.5V22H15V15H9V22H3V8.5L12 2Z" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <div className="login-panel-brand">Realty</div>
              <div className="login-panel-sub">CRM Platform</div>
            </div>
          </div>

          <div className="login-panel-headline">
            <h1>Your leads,<br /><em>always within reach.</em></h1>
          </div>

          <div className="login-panel-features">
            {FEATURES.map((f, i) => (
              <div key={i} className="login-feature animate-fade-up" style={{ animationDelay: `${200 + i * 80}ms` }}>
                <div className="login-feature-dot" />
                <span>{f}</span>
              </div>
            ))}
          </div>

          <div className="login-panel-sources">
            <p className="section-label">Integrates with</p>
            <div className="login-sources-row">
              {['99acres', 'MagicBricks', 'Housing.com'].map((s) => (
                <div key={s} className="login-source-chip">{s}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Corner accent */}
        <div className="login-panel-corner">
          <svg width="180" height="180" viewBox="0 0 180 180" fill="none" opacity="0.04">
            <path d="M90 10L170 60V160H110V110H70V160H10V60L90 10Z" stroke="currentColor" strokeWidth="2"/>
            <path d="M90 30L150 68V140H100V100H80V140H30V68L90 30Z" stroke="currentColor" strokeWidth="1"/>
          </svg>
        </div>
      </div>

      {/* Right — login form */}
      <div className="login-panel login-panel--right">
        <div className="login-form-wrap animate-fade-up" style={{ animationDelay: '60ms' }}>
          <div className="login-form-header">
            <h2 className="login-form-title">Welcome back</h2>
            <p className="login-form-subtitle">Sign in to your workspace</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="tenant" className="login-label">Workspace</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <BuildingIcon />
                </span>
                <input
                  id="tenant"
                  type="text"
                  className="login-input"
                  placeholder="your-org (leave blank for super admin)"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  autoComplete="organization"
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="email" className="login-label">Email address</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <MailIcon />
                </span>
                <input
                  id="email"
                  type="email"
                  className="login-input"
                  placeholder="you@organisation.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password" className="login-label">Password</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <LockIcon />
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="login-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-input-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {error && (
              <div className="login-error">
                <span className="login-error-icon">!</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              className={`login-submit ${loading ? 'login-submit--loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="login-spinner" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </form>

          <p className="login-footer-note">
            Access is managed by your organisation's administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  'Auto-capture leads from portals',
  'Assign & track across your team',
  'Configurable pipeline stages',
  'Multi-organisation support',
];

/* ─── Icons ──────────────────────────────────────────────────────────────── */

function BuildingIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="8" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="10" y="7" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 7.5h2M5 10h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M1.5 5l6.5 4 6.5-4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 7V5.5a3 3 0 0 1 6 0V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="8" cy="10.5" r="1" fill="currentColor"/>
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.3"/>
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M2 2l12 12M6.5 6.5A2 2 0 0 0 9.5 9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M4.3 4.3C2.7 5.4 1 8 1 8s2.5 5 7 5c1.4 0 2.7-.4 3.7-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M11.5 11.5C13.2 10.4 15 8 15 8s-2.5-5-7-5c-.8 0-1.6.1-2.3.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
